/**
 * useWebSocket Hook
 *
 * Provides a shared WebSocket connection with automatic reconnection,
 * message event handling, and request/response correlation.
 *
 * Features:
 * - Singleton connection pattern (shared across components)
 * - Exponential backoff reconnection
 * - Ping/pong keep-alive
 * - Message type subscription
 * - Request/response with timeout
 * - Connection state management
 *
 * @module hooks/useWebSocket
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// ============================================
// Constants
// ============================================

/**
 * WebSocket reconnection delay in milliseconds
 */
const WS_RECONNECT_DELAY_MS = 1000;

/**
 * Maximum reconnection delay in milliseconds
 */
const WS_MAX_RECONNECT_DELAY_MS = 30000;

/**
 * Ping interval for keep-alive in milliseconds
 */
const WS_PING_INTERVAL_MS = 30000;

/**
 * Request timeout in milliseconds
 */
const WS_REQUEST_TIMEOUT_MS = 10000;

/**
 * Maximum reconnection attempts before giving up (0 = infinite)
 */
const WS_MAX_RECONNECT_ATTEMPTS = 0;

/**
 * Connection states
 */
export const CONNECTION_STATE = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed'
};

/**
 * WebSocket message types
 */
export const WS_MESSAGE_TYPES = {
  // Client -> Server
  SUBSCRIBE: 'subscribe',
  UNSUBSCRIBE: 'unsubscribe',
  SEND_MESSAGE: 'send_message',
  SEND_DM: 'send_dm',
  MARK_READ: 'mark_read',
  QUEUE_NEXT: 'queue_next',
  STATUS_UPDATE: 'status_update',
  PING: 'ping',

  // Server -> Client
  SUBSCRIBED: 'subscribed',
  UNSUBSCRIBED: 'unsubscribed',
  MESSAGE: 'message',
  DM: 'dm',
  MESSAGE_SENT: 'message_sent',
  DM_SENT: 'dm_sent',
  MARKED_READ: 'marked_read',
  QUEUE_PROCESSED: 'queue_processed',
  QUEUE_NOTIFICATION: 'queue_notification',
  AGENT_STATUS: 'agent_status',
  STATUS_UPDATED: 'status_updated',
  PRESENCE: 'presence',
  ERROR: 'error',
  PONG: 'pong'
};

// ============================================
// Singleton Connection Manager
// ============================================

/**
 * Global WebSocket connection manager
 * Maintains a single connection shared across all hook instances
 */
class WebSocketManager {
  constructor() {
    this.ws = null;
    this.state = CONNECTION_STATE.DISCONNECTED;
    this.reconnectAttempts = 0;
    this.reconnectTimeout = null;
    this.pingInterval = null;
    this.subscribers = new Set();
    this.messageHandlers = new Map();
    this.pendingRequests = new Map();
    this.url = null;
    this.autoConnect = true;
    this.manualDisconnect = false;
  }

  /**
   * Get the current WebSocket URL
   * @returns {string} WebSocket URL
   */
  getDefaultUrl() {
    if (typeof window === 'undefined') {
      return 'ws://localhost:3001/ws/chat';
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;

    // Determine backend host:
    // - VITE_DEV_BACKEND_HOST set → use that (for remote dev like dev.agents.sneakercloud.de)
    // - Local dev (localhost:5173) → direct connect to backend on :3001
    // - Production → use nginx proxy (same host)
    let backendHost;
    if (import.meta.env.VITE_DEV_BACKEND_HOST) {
      // Remote development - connect directly to backend host
      backendHost = import.meta.env.VITE_DEV_BACKEND_HOST;
      console.log('[WSManager] Using VITE_DEV_BACKEND_HOST:', backendHost);
    } else if (import.meta.env.DEV && host.includes('localhost')) {
      // Local development without proxy
      backendHost = host.replace(/:\d+$/, ':3001');
    } else {
      // Production - use current host, nginx will proxy
      backendHost = host;
    }

    const url = `${protocol}//${backendHost}/ws`;
    console.log('[WSManager] getDefaultUrl:', url);
    return url;
  }

  /**
   * Add a subscriber
   * @param {Function} callback - Callback for state changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.add(callback);
    console.log('[WSManager] Subscriber added, total:', this.subscribers.size);

    // Immediately notify the new subscriber of current state
    callback({
      state: this.state,
      error: this.error || null
    });

    // Auto-connect if this is the first subscriber
    if (this.subscribers.size === 1 && this.autoConnect && !this.ws) {
      console.log('[WSManager] First subscriber, auto-connecting...');
      this.connect();
    }

    return () => {
      this.subscribers.delete(callback);
      console.log('[WSManager] Subscriber removed, remaining:', this.subscribers.size);
      // Don't auto-disconnect - React StrictMode causes mount/unmount/mount cycles
      // which would disconnect the WebSocket prematurely.
      // The connection will be cleaned up when the page unloads.
    };
  }

  /**
   * Notify all subscribers of state change
   * @param {object} data - State data to notify
   */
  notifySubscribers(data) {
    this.subscribers.forEach((callback) => {
      try {
        callback(data);
      } catch {
        // Ignore subscriber errors
      }
    });
  }

  /**
   * Add a message type handler
   * @param {string} type - Message type to handle
   * @param {Function} handler - Handler function
   * @returns {Function} Unsubscribe function
   */
  addMessageHandler(type, handler) {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }
    this.messageHandlers.get(type).add(handler);

    return () => {
      const handlers = this.messageHandlers.get(type);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.messageHandlers.delete(type);
        }
      }
    };
  }

  /**
   * Connect to WebSocket server
   * @param {string} [url] - Optional custom URL
   */
  connect(url) {
    console.log('[WSManager] connect() called, current readyState:', this.ws?.readyState);

    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[WSManager] Already connected, skipping');
      return;
    }

    if (this.ws?.readyState === WebSocket.CONNECTING) {
      console.log('[WSManager] Already connecting, skipping');
      return;
    }

    this.manualDisconnect = false;
    this.url = url || this.url || this.getDefaultUrl();
    console.log('[WSManager] Connecting to:', this.url);

    this.state =
      this.reconnectAttempts > 0
        ? CONNECTION_STATE.RECONNECTING
        : CONNECTION_STATE.CONNECTING;
    this.notifySubscribers({ state: this.state });

    try {
      this.ws = new WebSocket(this.url);
      this.setupEventHandlers();
      console.log('[WSManager] WebSocket created, waiting for open...');
    } catch (error) {
      console.error('[WSManager] Failed to create WebSocket:', error);
      this.state = CONNECTION_STATE.FAILED;
      this.error = error.message || 'Failed to create WebSocket connection';
      this.notifySubscribers({ state: this.state, error: this.error });
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  setupEventHandlers() {
    this.ws.onopen = () => {
      console.log('[WSManager] WebSocket OPEN! Setting state to CONNECTED');
      this.state = CONNECTION_STATE.CONNECTED;
      this.reconnectAttempts = 0;
      this.error = null;
      console.log('[WSManager] Notifying', this.subscribers.size, 'subscribers of CONNECTED state');
      this.notifySubscribers({ state: this.state });

      // Start ping interval
      this.startPingInterval();

      // Notify handlers of connection
      this.dispatchMessage({ type: '_connected' });
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event);
    };

    this.ws.onerror = (event) => {
      console.error('[WSManager] WebSocket ERROR:', event);
      this.error = 'WebSocket connection error';
      this.notifySubscribers({ state: this.state, error: this.error });
    };

    this.ws.onclose = (event) => {
      console.log('[WSManager] WebSocket CLOSED, code:', event.code, 'reason:', event.reason);
      this.stopPingInterval();
      this.ws = null;

      // If manually disconnected, don't reconnect
      if (this.manualDisconnect) {
        this.state = CONNECTION_STATE.DISCONNECTED;
        this.notifySubscribers({ state: this.state });
        return;
      }

      // Notify handlers of disconnection
      this.dispatchMessage({ type: '_disconnected', code: event.code, reason: event.reason });

      // Attempt reconnection
      this.scheduleReconnect();
    };
  }

  /**
   * Handle incoming WebSocket message
   * @param {MessageEvent} event - WebSocket message event
   */
  handleMessage(event) {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }

    // Handle pending request responses
    if (data.requestId && this.pendingRequests.has(data.requestId)) {
      const { resolve, reject, timeout } = this.pendingRequests.get(data.requestId);
      clearTimeout(timeout);
      this.pendingRequests.delete(data.requestId);

      if (data.type === WS_MESSAGE_TYPES.ERROR) {
        reject(new Error(data.message || 'Unknown error'));
      } else {
        resolve(data);
      }
    }

    // Dispatch to message handlers
    this.dispatchMessage(data);
  }

  /**
   * Dispatch message to registered handlers
   * @param {object} data - Message data
   */
  dispatchMessage(data) {
    const type = data.type;
    const handlers = this.messageHandlers.get(type);

    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch {
          // Ignore handler errors
        }
      });
    }

    // Also dispatch to wildcard handlers
    const wildcardHandlers = this.messageHandlers.get('*');
    if (wildcardHandlers) {
      wildcardHandlers.forEach((handler) => {
        try {
          handler(data);
        } catch {
          // Ignore handler errors
        }
      });
    }
  }

  /**
   * Start ping interval for keep-alive
   */
  startPingInterval() {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: WS_MESSAGE_TYPES.PING }));
      }
    }, WS_PING_INTERVAL_MS);
  }

  /**
   * Stop ping interval
   */
  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    // Check max attempts
    if (
      WS_MAX_RECONNECT_ATTEMPTS > 0 &&
      this.reconnectAttempts >= WS_MAX_RECONNECT_ATTEMPTS
    ) {
      this.state = CONNECTION_STATE.FAILED;
      this.error = 'Maximum reconnection attempts reached';
      this.notifySubscribers({ state: this.state, error: this.error });
      return;
    }

    this.reconnectAttempts += 1;
    this.state = CONNECTION_STATE.RECONNECTING;
    this.notifySubscribers({ state: this.state });

    // Calculate delay with exponential backoff
    const delay = Math.min(
      WS_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts - 1),
      WS_MAX_RECONNECT_DELAY_MS
    );

    this.reconnectTimeout = setTimeout(() => {
      if (!this.manualDisconnect && this.subscribers.size > 0) {
        this.connect();
      }
    }, delay);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.manualDisconnect = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.stopPingInterval();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    // Reject all pending requests
    this.pendingRequests.forEach(({ reject, timeout }) => {
      clearTimeout(timeout);
      reject(new Error('Connection closed'));
    });
    this.pendingRequests.clear();

    this.state = CONNECTION_STATE.DISCONNECTED;
    this.reconnectAttempts = 0;
    this.notifySubscribers({ state: this.state });
  }

  /**
   * Send a message
   * @param {object} message - Message to send
   */
  send(message) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    this.ws.send(JSON.stringify(message));
  }

  /**
   * Send a message and wait for response
   * @param {object} message - Message to send
   * @param {number} [timeout] - Timeout in milliseconds
   * @returns {Promise<object>} Response from server
   */
  request(message, timeout = WS_REQUEST_TIMEOUT_MS) {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const requestId = generateRequestId();
      const messageWithId = { ...message, requestId };

      // Setup timeout
      const timeoutId = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error('Request timeout'));
      }, timeout);

      // Store pending request
      this.pendingRequests.set(requestId, {
        resolve,
        reject,
        timeout: timeoutId
      });

      try {
        this.ws.send(JSON.stringify(messageWithId));
      } catch (error) {
        clearTimeout(timeoutId);
        this.pendingRequests.delete(requestId);
        reject(error);
      }
    });
  }

  /**
   * Check if connected
   * @returns {boolean} True if connected
   */
  isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get current state
   * @returns {string} Current connection state
   */
  getState() {
    return this.state;
  }

  /**
   * Reset connection state for testing
   */
  reset() {
    this.disconnect();
    this.subscribers.clear();
    this.messageHandlers.clear();
    this.url = null;
    this.error = null;
  }
}

// ============================================
// Helper Functions
// ============================================

/**
 * Generate a unique request ID
 * @returns {string} Unique request ID
 */
function generateRequestId() {
  return `req_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================
// Singleton Instance
// ============================================

/**
 * Global WebSocket manager instance
 */
let wsManager = null;

/**
 * Get the WebSocket manager singleton
 * @returns {WebSocketManager} WebSocket manager instance
 */
export function getWebSocketManager() {
  if (!wsManager) {
    wsManager = new WebSocketManager();
  }
  return wsManager;
}

/**
 * Reset the WebSocket manager (for testing)
 */
export function resetWebSocketManager() {
  if (wsManager) {
    wsManager.reset();
    wsManager = null;
  }
}

// ============================================
// Hook
// ============================================

/**
 * useWebSocket Hook
 *
 * Provides access to a shared WebSocket connection with automatic
 * reconnection, message subscription, and request/response handling.
 *
 * @param {object} [options] - Hook options
 * @param {string} [options.url] - Custom WebSocket URL
 * @param {boolean} [options.autoConnect=true] - Auto-connect on mount
 * @param {object} [options.handlers] - Message type handlers { type: handler }
 *
 * @returns {object} Hook return value
 * @returns {string} returns.state - Current connection state
 * @returns {boolean} returns.isConnected - Whether WebSocket is connected
 * @returns {string|null} returns.error - Current error message
 * @returns {Function} returns.connect - Connect to server
 * @returns {Function} returns.disconnect - Disconnect from server
 * @returns {Function} returns.send - Send a message (fire and forget)
 * @returns {Function} returns.request - Send a message and wait for response
 * @returns {Function} returns.subscribe - Subscribe to message type
 *
 * @example
 * // Basic usage
 * const { state, isConnected, send } = useWebSocket();
 *
 * // With message handlers
 * const { state, isConnected } = useWebSocket({
 *   handlers: {
 *     message: (data) => console.log('Message:', data),
 *     error: (data) => console.error('Error:', data)
 *   }
 * });
 *
 * // With request/response
 * const { request } = useWebSocket();
 * const response = await request({ type: 'subscribe', channelId: '123' });
 */
export function useWebSocket(options = {}) {
  console.log('[useWebSocket] Hook called with options:', options);

  const {
    url,
    autoConnect = true,
    handlers = {}
  } = options;

  const [state, setState] = useState(CONNECTION_STATE.DISCONNECTED);
  const [error, setError] = useState(null);

  const manager = useMemo(() => {
    console.log('[useWebSocket] Getting WebSocket manager singleton');
    return getWebSocketManager();
  }, []);
  const handlersRef = useRef(handlers);

  // Update handlers ref on change
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  // Subscribe to connection state
  useEffect(() => {
    console.log('[useWebSocket] Subscribing to manager state updates');
    const unsubscribe = manager.subscribe((data) => {
      console.log('[useWebSocket] Received state update:', data.state);
      setState(data.state);
      setError(data.error || null);
    });

    return () => {
      console.log('[useWebSocket] Unsubscribing from manager');
      unsubscribe();
    };
  }, [manager]);

  // Register message handlers
  useEffect(() => {
    const unsubscribers = [];

    Object.entries(handlersRef.current).forEach(([type, handler]) => {
      if (typeof handler === 'function') {
        const unsubscribe = manager.addMessageHandler(type, handler);
        unsubscribers.push(unsubscribe);
      }
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [manager, handlers]);

  // Auto-connect on mount if specified
  useEffect(() => {
    if (autoConnect && url) {
      manager.connect(url);
    }
  }, [manager, autoConnect, url]);

  /**
   * Connect to WebSocket server
   * @param {string} [customUrl] - Optional custom URL
   */
  const connect = useCallback(
    (customUrl) => {
      manager.connect(customUrl || url);
    },
    [manager, url]
  );

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    manager.disconnect();
  }, [manager]);

  /**
   * Send a message (fire and forget)
   * @param {object} message - Message to send
   */
  const send = useCallback(
    (message) => {
      manager.send(message);
    },
    [manager]
  );

  /**
   * Send a message and wait for response
   * @param {object} message - Message to send
   * @param {number} [timeout] - Timeout in milliseconds
   * @returns {Promise<object>} Response from server
   */
  const request = useCallback(
    (message, timeout) => {
      return manager.request(message, timeout);
    },
    [manager]
  );

  /**
   * Subscribe to a message type
   * @param {string} type - Message type to subscribe to
   * @param {Function} handler - Handler function
   * @returns {Function} Unsubscribe function
   */
  const subscribe = useCallback(
    (type, handler) => {
      return manager.addMessageHandler(type, handler);
    },
    [manager]
  );

  /**
   * Check if connected
   * @returns {boolean} True if connected
   */
  const isConnected = useMemo(() => {
    return state === CONNECTION_STATE.CONNECTED;
  }, [state]);

  return {
    // State
    state,
    isConnected,
    error,

    // Actions
    connect,
    disconnect,
    send,
    request,
    subscribe,

    // Constants (for convenience)
    CONNECTION_STATE,
    WS_MESSAGE_TYPES
  };
}

// ============================================
// Convenience Hooks
// ============================================

/**
 * useWebSocketMessage Hook
 *
 * Subscribe to a specific message type.
 *
 * @param {string} type - Message type to subscribe to
 * @param {Function} handler - Handler function
 *
 * @example
 * useWebSocketMessage('message', (data) => {
 *   console.log('New message:', data);
 * });
 */
export function useWebSocketMessage(type, handler) {
  const manager = useMemo(() => getWebSocketManager(), []);
  const handlerRef = useRef(handler);

  // Update handler ref on change
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  // Subscribe to message type
  useEffect(() => {
    const wrappedHandler = (data) => {
      if (handlerRef.current) {
        handlerRef.current(data);
      }
    };

    const unsubscribe = manager.addMessageHandler(type, wrappedHandler);
    return unsubscribe;
  }, [manager, type]);
}

/**
 * useWebSocketState Hook
 *
 * Get just the connection state without any handlers.
 *
 * @returns {object} Hook return value
 * @returns {string} returns.state - Current connection state
 * @returns {boolean} returns.isConnected - Whether WebSocket is connected
 * @returns {string|null} returns.error - Current error message
 *
 * @example
 * const { isConnected, error } = useWebSocketState();
 */
export function useWebSocketState() {
  const [state, setState] = useState(CONNECTION_STATE.DISCONNECTED);
  const [error, setError] = useState(null);

  const manager = useMemo(() => getWebSocketManager(), []);

  useEffect(() => {
    const unsubscribe = manager.subscribe((data) => {
      setState(data.state);
      setError(data.error || null);
    });

    return unsubscribe;
  }, [manager]);

  return {
    state,
    isConnected: state === CONNECTION_STATE.CONNECTED,
    error
  };
}

// ============================================
// Default Export
// ============================================

export default useWebSocket;
